import { useQuery } from 'react-query'

import { setFatalError } from '../../FatalError/FatalError'
import isHeadless from '../../headless/isHeadless'
import queryClient from '../../queryClient'
import { request } from '../api/legacyApi'
import {
  HTTPMethod,
  type APIResponse,
} from '../../sharedLegacy/utils/api.types'

import { getLiveId, getPresenterToken } from './roomInfo'

type HexColor = `#${string}`

type SiteDataDTO = {
  site_id: string
  product_key: string
  allow_signup_p: number
  site_key: string
  license_id: string
  locale: string
  domain: string
  site_name: string
  company_name: string
  site_url: string
  http_url: string
  https_url: string
  environment: string
  date_format: string
  time_format: string
  files_folder: string
  enable_webinar_polls_p: boolean
  enable_webinar_engagement_tab_p: boolean
  enable_webinar_overlays_p: boolean
  enable_webinar_promo_overlays_p: boolean
  mischung_recording_split_seconds: string
  presentation_webinarRoomAccentColor: HexColor
  presentation_brandPrimary: string
  presentation_searchMenu: string
  presentation_dropdownMenu: string
  presentation_headingsFont__font_name: string
  presentation_headingsFont__font_family: string
  presentation_headingsFont__font_import_url: string
  presentation_headingsFont: string
  presentation_logo: string
  presentation_mailHeaderImage: string
  presentation_landingPageMenus: string
  presentation_playicon: string
  presentation_brandSecondary: string
  presentation_webinarRoomSidebarBackgroundColor: HexColor
  presentation_siteFont__font_name: string
  presentation_siteFont__font_family: string
  presentation_siteFont__font_import_url: string
  presentation_siteFont: string
  presentation_webinarRoomTabBackgroundColor: HexColor
  presentation_webinarHubStyle: string
  presentation_webinarRegistrationStyle: string
  presentation_webinarRoomLogo: string
  max_sites: string
  product_grade: string
  enable_live_p: boolean
  custom_logo_p: boolean
  custom_logo_url: string
  page_layout: string
  site_type: string
  subscription_id: string
  creation_date_epoch: string
  current_sites: string
  current_videos: string
  current_users: string
  max_videos: string
  max_users: string
}

type SiteDataParams = {
  live_id: number
  presenter_token?: string
  include_presentation_p: boolean
}

const siteQuery = {
  queryKey: ['site_data'],
  queryFn: async () => {
    const liveId = getLiveId()
    const presenterToken = getPresenterToken()

    const { data } = await request<APIResponse<SiteDataDTO>>({
      url: '/api/2/site/get',
      method: HTTPMethod.GET,
      data: {
        live_id: liveId,
        presenter_token: presenterToken,
        include_presentation_p: true,
      } satisfies SiteDataParams,
    })
    return data
  },
  cacheTime: Infinity,
  staleTime: Infinity,
  onError: (err: unknown) => {
    setFatalError('FETCH_SITE_DATA_ERROR', {
      reload: isHeadless ? 'yes' : 'ask',
      reason: err,
    })
  },
}

const useSiteQuery = () => {
  return useQuery(siteQuery)
}

const getSiteData = () => {
  return queryClient.fetchQuery(siteQuery)
}

export { useSiteQuery, getSiteData }
